import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClientAuth } from "../lib/clientAuth";
import { ButtonSpinner, PageSpinner } from "../components/Loading";

// Two responsibilities in one page:
//
//  1. Magic-link landing — when the URL has ?token=… we consume it via
//     /api/c/consume and redirect to /c/dashboard on success.
//  2. Manual email entry — for clients who lost their email link or are
//     on a different device. Submit posts to /api/c/login which always
//     responds 200 (no leak on miss).

type Phase =
  | { kind: "form" }
  | { kind: "submitting" }
  | { kind: "sent" }
  | { kind: "consuming" }
  | { kind: "consume_error"; message: string };

export function ClientLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, requestLogin, consumeToken } = useClientAuth();
  const token = searchParams.get("token");
  const shareToken = searchParams.get("share") ?? undefined;

  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "form" });

  // Magic-link arm.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setPhase({ kind: "consuming" });
    consumeToken(token, shareToken)
      .then(() => {
        if (!cancelled) navigate("/c/dashboard", { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { status?: number }).status === 410
            ? "This sign-in link has expired or already been used. Request a new one below."
            : "Couldn’t sign you in with that link. Try requesting a fresh one.";
        setPhase({ kind: "consume_error", message: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [token, shareToken, consumeToken, navigate]);

  // Already signed in — kick to dashboard.
  useEffect(() => {
    if (state.status === "authed") {
      navigate("/c/dashboard", { replace: true });
    }
  }, [state.status, navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPhase({ kind: "submitting" });
    try {
      await requestLogin(email.trim());
      setPhase({ kind: "sent" });
    } catch {
      setPhase({ kind: "sent" }); // intentional: same UX on miss to avoid leak
    }
  }

  return (
    <section className="mx-auto max-w-md px-6 py-16">
      <h1 className="text-2xl font-semibold tracking-tight">Sign in to your review</h1>
      <p className="mt-2 text-sm text-neutral-600">
        Enter the email Avista used to invite you. We’ll send you a sign-in link.
      </p>

      {phase.kind === "consuming" ? (
        <div className="mt-8">
          <PageSpinner label="Signing you in" />
        </div>
      ) : phase.kind === "sent" ? (
        <div className="mt-8 rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
          If <span className="font-medium">{email}</span> is on the collaborator
          list for any Clarity project, we just sent a sign-in link. Check your inbox.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          {phase.kind === "consume_error" ? (
            <p className="text-sm text-red-600">{phase.message}</p>
          ) : null}
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={phase.kind === "submitting"}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
            autoFocus
          />
          <button
            type="submit"
            disabled={phase.kind === "submitting" || !email.trim()}
            className="inline-flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
          >
            {phase.kind === "submitting" ? (
              <ButtonSpinner label="Sending" />
            ) : (
              "Send sign-in link"
            )}
          </button>
        </form>
      )}
    </section>
  );
}
